/**
 * @module Components.Selectbox
 *
 */
import React, { useRef, useState, useMemo } from "react";
import { components } from "react-select";
import { OptionProps } from "react-select/src/components/Option";
import { MultiValueProps } from "react-select/src/components/MultiValue";
import { NoticeProps } from "react-select/src/components/Menu";
import { useStoreActions } from "app/utils/hooks";
import { AuthService } from "app/services";
import SelectBox, {
  StateManager,
  SelectProps,
  OptionsType,
  DefaultOption,
} from "app/components/inputs/SelectBox/Base";
import { uuid } from "app/utils/uuid";
import { cls } from "app/utils";
import { UserModel } from "app/models";
import { fetchConnections } from "app/actions/connection";
import { CreateNewForm } from "./helpers";
import UserAvatar from "app/components/UserAvatar";
import ManagedByNote from "app/components/ManagedByNote";
import { Loading } from "app/components/Wrappers";
import { findJack, createMemberJack } from "app/utils/appcues";
import { nestFamilyConnections } from "app/utils/familyConnections";
import Styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";

const Auth: any = AuthService;

export interface UserOption extends DefaultOption {
  model: UserModel;
}

interface ComponentOpts {
  isCreatable: boolean;
  selectState: React.MutableRefObject<StateManager<UserOption>>;
  noOptionsMessage: (value: { inputValue: string }) => string;
  onSaveNewOption: (value: UserOption) => void;
  addAthleteBtn: boolean;
  showNoOptionsForm: boolean;
  showForm: boolean;
  setShowNoOptionsForm: (v: boolean) => void;
  setShowForm: (v: boolean) => void;
}

interface PropsDef extends SelectProps<UserOption> {
  value?: any; // UserModel[];
  defaultValue?: any; // UserModel[];
  excludeOptions?: UserModel[];
  hideSelected?: boolean;
  includeJack?: boolean;
}

const normalizeValue = (val?: UserModel[] | null): UserOption[] | null => {
  if (val == null) {
    return null;
  }

  if (Array.isArray(val)) {
    return val.map((user) => createUserOption(user));
  } else {
    return [createUserOption(val)];
  }
};

const ADD_NEW_ATHLETE = "add-new-athlete-btn";

const customButtonOption = (label: string) => ({
  value: ADD_NEW_ATHLETE,
  label , // "Add a New Athlete"
  className: "custom-button-option",
});

/**
 * @class SpaceUserSelect
 *
 */
export default function SpaceUserSelect(props: PropsDef) {
  const { t } = useTranslation();
  const {
    className,
    value,
    defaultValue,
    isCreatable,
    excludeOptions,
    noOptionsMessage,
    hideSelected,
    includeJack,
    addAthleteBtn = false,
    ...rest
  } = props;

  const { connection } = useStoreActions({ connection: fetchConnections });
  const selectState = useRef<StateManager<UserOption> | null>(null);
  const [__options, setOptions] = useState<OptionsType<UserOption>>([]);
  const [showNoOptionsForm, setShowNoOptionsForm] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // load connections from api

  const buildOptions = (): void => {
    if (connection.data != null && !showForm) {
      const exclude = excludeOptions || [];

      let users: UserModel[] = connection.data;
      if (exclude.length) {
        users = users.filter((user: UserModel) => {
          return exclude.find((ex: UserModel) => ex.id === user.id) == null;
        });
      }
      if (includeJack && Auth.user && Auth.user.kind === "coach") {
        if (findJack(users, "email") == null) {
          users.unshift(createMemberJack());
        }
      }
      if (isCreatable) {
        const newOptions = nestFamilyConnections(users).map((user) =>
          createUserOption(user)
        );
        newOptions.unshift(customButtonOption(t("Add a New Athlete")) as any);
        setOptions(newOptions);
      }
    } else {
      setOptions([]);
    }
  };

  useMemo(() => {
    buildOptions();
  }, [connection.data, excludeOptions, Auth.user, includeJack, showForm]);

  const onSaveNewOption = (value: UserOption) => {
    if (selectState.current != null) {
      selectState.current.createOption([value]);
    }
  };

  const handleClose = () => {
    setShowNoOptionsForm(false);
    setShowForm(false);

    // A click on the search box closes the menu even while the add-athlete
    // form inside it has focus. That focus goes with the menu, and react-select,
    // which never saw the search lose it, wouldn't give it back on a click: the
    // field took no typing until the page was reloaded. Back to the search.
    const state = selectState.current;
    const active = document.activeElement;
    if (
      state != null &&
      state.outerRef != null &&
      state.outerRef.contains(active) &&
      !(state.controlRef != null && state.controlRef.contains(active))
    ) {
      state.focus();
    }
  };

  // The "Add a New Athlete" button rides along as the first option, so it is
  // what react-select highlights on open, and Enter, Tab or Space would pick
  // it: an option with no athlete behind it, which crashed the page. Its own
  // button still opens the form on a click.
  const isOptionDisabled = (option: UserOption) =>
    option.value === ADD_NEW_ATHLETE;

  // react-select remounts any part whose component changes, so building these
  // on every render swapped the input for a new one, losing its focus, each
  // time the field re-rendered (as when the connections finish loading). They
  // are built once and read whatever changes through this ref.
  const componentOpts = useRef<ComponentOpts | null>(null);
  componentOpts.current = {
    isCreatable,
    selectState,
    onSaveNewOption,
    noOptionsMessage,
    addAthleteBtn,
    showNoOptionsForm,
    showForm,
    setShowNoOptionsForm,
    setShowForm,
  };
  const comps = useMemo(() => createComponents(componentOpts), []);

  return (
    <SelectBox
      ref={selectState}
      className={cls("user-select", className)}
      placeholder={t("Search by name or email")}
      controlShouldRenderValue={!hideSelected}
      isClearable={true}
      isCreatable={false}
      isMulti={true}
      isLoading={connection.pending}
      isOptionDisabled={isOptionDisabled}
      onMenuClose={handleClose}
      options={__options}
      value={normalizeValue(value)}
      defaultValue={normalizeValue(defaultValue)}
      components={comps}
      {...rest}
    />
  );
}

function createUserOption(user: UserModel): UserOption {
  return {
    value: user.id || uuid(),
    label: `${user.displayName} ${user.email}`,
    model: user,
  };
}

interface AddNewProps {
  label: string;
  showTitle?: boolean;
  onClick: (event: React.FormEvent<HTMLButtonElement>) => void;
}

export function AddNew(props: AddNewProps) {
  const { t } = useTranslation();

  const title = (
    <h3 className="needsclick">
      {t("No results found")}
    </h3>
  );
  return (
    <div
      className={cls("add-new-handle", Styles.noOptionsButton, "needsclick")}
      contentEditable={false}
    >
      {props.showTitle ? title : ""}
      <button className="btn btn-primary needsclick" onClick={props.onClick}>
        {props.label}
      </button>
    </div>
  );
}

function createComponents(opts: React.MutableRefObject<ComponentOpts>) {
  function NoOptionsMessage(props: NoticeProps<UserOption>) {
    const { t } = useTranslation();
    const {
      isCreatable,
      selectState,
      onSaveNewOption,
      noOptionsMessage,
      showNoOptionsForm,
      showForm,
      setShowNoOptionsForm,
      setShowForm,
    } = opts.current;
    const inputValue =
      selectState.current && selectState.current.getInputValue();

    const createNewOption = (event: React.FormEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setShowNoOptionsForm(true);
      return false;
    };

    const formatCreateLabel = () => {
      if (noOptionsMessage != null) {
        return noOptionsMessage({ inputValue });
      }
      return t("No results found");
    };

    // Cancel takes the form, and the button that had focus, away with it, so
    // focus drops to the page without react-select hearing of it, and the
    // field then won't take focus on a click. Back to the search instead.
    const focusSearch = () => {
      if (selectState.current != null) {
        selectState.current.focus();
      }
    };

    const saveNewOption = (user: UserModel) => {
      if (onSaveNewOption != null) {
        onSaveNewOption(createUserOption(user));
      }
      setShowNoOptionsForm(false);
      setShowForm(false);
    };

    if (isCreatable) {
      const rest = { ...props };
      delete rest.children;

      if (showForm) {
        return (
          <>
            <components.NoOptionsMessage {...rest}>
              <CreateNewForm
                getLabel={formatCreateLabel}
                onDone={saveNewOption}
                onCancel={() => {
                  setShowForm(false);
                  focusSearch();
                }}
                inputValue={inputValue}
              />
            </components.NoOptionsMessage>
          </>
        );
      }

      if (showNoOptionsForm) {
        return (
          <>
            <components.NoOptionsMessage {...rest}>
              <CreateNewForm
                getLabel={formatCreateLabel}
                onDone={saveNewOption}
                onCancel={() => {
                  setShowNoOptionsForm(false);
                  focusSearch();
                }}
                inputValue={inputValue}
              />
            </components.NoOptionsMessage>
          </>
        );
      }
      return (
        <components.NoOptionsMessage {...rest}>
          <AddNew
            showTitle={true}
            label={formatCreateLabel()}
            onClick={createNewOption}
          />
        </components.NoOptionsMessage>
      );
    }
    return <components.NoOptionsMessage {...props} />;
  }

  // Shown while the connections load, before there's anything to list: the
  // /spaces page spinner rather than react-select's "Loading..." text.
  function LoadingMessage(props: NoticeProps<UserOption>) {
    const rest = { ...props };
    delete rest.children;

    return (
      <components.LoadingMessage {...rest}>
        <Loading isLoading loadType="spinner" />
      </components.LoadingMessage>
    );
  }

  // The spinner in the menu is the loading state; react-select's dots in the
  // field would be a second one.
  function LoadingIndicator(): null {
    return null;
  }

  function Option(props: OptionProps<UserOption>) {
    const { t } = useTranslation();
    const { addAthleteBtn, showForm, setShowForm, setShowNoOptionsForm } =
      opts.current;
    const rest = { ...props };
    delete rest.children;

    const { data } = props;

    const createNewOption = (event: React.FormEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setShowForm(true);
      setShowNoOptionsForm(false);
    };

    if (data.value === customButtonOption(t("Add a New Athlete")).value) {
      return (
        !showForm &&
        addAthleteBtn && (
          <div
            className={cls("menu-item needsclick", Styles.custom_option_btn)}
            id={data.value}
          >
            <button
              className="btn btn-primary needsclick"
              onClick={createNewOption}
            >
              {data.label}
            </button>
          </div>
        )
      );
    }

    const user = props.data.model;
    if (user == null) {
      return <components.Option {...rest}>{props.children}</components.Option>;
    }

    return (
      <components.Option {...rest}>
        <div
          className={cls("menu-item needsclick", Styles.user_option)}
          id={user.id}
        >
          <div className="profile-block user-select-user profile-block-default needsclick">
            <UserAvatar
              className="avatar needsclick"
              userName={user.name}
              srcName={user.avatar as string}
            />
            <div className="profile-block-details needsclick">
              <div className="profile-block-title needsclick">
                {user.displayName}
              </div>
              <div className="profile-block-summary needsclick">
                {user.email || user.phoneNormalized}
              </div>
              <ManagedByNote
                user={user}
                className="profile-block-summary needsclick"
              />
            </div>
          </div>
        </div>
      </components.Option>
    );
  }

  function MultiValueLabel(props: MultiValueProps<UserOption>) {
    const rest = { ...props };
    delete rest.children;

    const user = props.data.model;
    return (
      <div className={cls(Styles.selectedUser, "needsclick")} key={user.id}>
        <components.MultiValueLabel {...rest}>
          <UserAvatar
            className="avatar needsclick"
            srcName={user.avatar as string}
          />
          <span className="needsclick">{user.displayName}</span>
        </components.MultiValueLabel>
      </div>
    );
  }

  function Menu(props: any) {
    const { onMouseDown } = props.innerProps;
    props.innerProps.onMouseDown = (evt: React.MouseEvent<HTMLDivElement>) => {
      const { showNoOptionsForm, showForm } = opts.current;
      if (evt.button !== 0) {
        return evt;
      }

      let isButtonClick: boolean = false;
      if (evt.relatedTarget != null) {
        isButtonClick = /btn/.test(
          (evt.relatedTarget as HTMLElement).className
        );
      }

      if (
        evt.currentTarget.contains(evt.target as HTMLElement) &&
        (showNoOptionsForm || showForm || isButtonClick)
      ) {
        return evt;
      }

      return onMouseDown(evt);
    };

    props.innerProps.contentEditable = false;

    return <components.Menu {...props} />;
  }

  function Input({ onBlur, ...props }: any) {
    props.onBlur = (evt: React.MouseEvent<HTMLDivElement>): any => {
      const { selectState, showForm } = opts.current;
      const inputValue =
        selectState.current && selectState.current.getInputValue();

      let isButtonClick: boolean = false;
      if (evt.relatedTarget != null) {
        isButtonClick = /btn/.test(
          (evt.relatedTarget as HTMLElement).className
        );
      }

      if (
        inputValue != null &&
        inputValue.length &&
        (showForm || isButtonClick)
      ) {
        evt.preventDefault();
        evt.stopPropagation();
        return false;
      }

      // Focus moving within the select, into the add-athlete form say, isn't
      // leaving it. Any other blur has to reach react-select: until it does,
      // react-select takes the input as still focused, and a click on the
      // field then opens the menu without focusing the input to type in.
      const outer = selectState.current && selectState.current.outerRef;
      if (outer != null && outer.contains(evt.relatedTarget as Node)) {
        return false;
      }
      return onBlur(evt);
    };

    props.onKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
      // With nothing typed yet, react-select takes Space as "pick the
      // highlighted option" instead of a character. This is a search field:
      // kept from react-select, a Space is typed like any other.
      if (evt.key === " ") {
        evt.stopPropagation();
        return;
      }

      const { selectState, setShowForm, setShowNoOptionsForm } = opts.current;
      const select = selectState.current && selectState.current.selectRef;
      if (select == null) {
        return;
      }
      const focused = (select as any).select.state.focusedOption;
      const onAddNew = focused != null && focused.value === ADD_NEW_ATHLETE;

      // The disabled "Add a New Athlete" is what's highlighted when the menu
      // opens, and react-select's Enter does nothing with it. Enter opens the
      // form instead, as a click on the button does.
      if (evt.key === "Enter" && onAddNew) {
        evt.preventDefault();
        evt.stopPropagation();
        setShowForm(true);
        setShowNoOptionsForm(false);
        return;
      }

      // With nothing to pick, the button or no match highlighted, react-select
      // either holds Tab or lets it land inside the open menu (a button there,
      // or the scrolling list itself), leaving the search stuck either way.
      // Closing the menu first, Tab goes on to the next field.
      if (evt.key === "Tab" && (focused == null || onAddNew)) {
        evt.stopPropagation();
        (select as any).onMenuClose();
      }
    };
    return <components.Input {...props} />;
  }

  return {
    Option,
    NoOptionsMessage,
    LoadingMessage,
    LoadingIndicator,
    MultiValueLabel,
    Input,
    Menu,
  };
}
